/**
 * Web component for handling dynamic price display for color swatches on product cards
 * - Shows price range when no swatch is selected (if variants are on sale)
 * - Shows compare_at_price with strikethrough + sale price in ember when sale swatch is hovered/selected
 * - Shows full price when non-sale swatch is hovered/selected
 */

class SwatchPriceHandler extends HTMLElement {
  constructor() {
    super();
    this.selectedSwatch = null;
    this.swatches = [];
    this.defaultMinPrice = 0;
    this.defaultMaxPrice = 0;
    this.defaultMinCompare = 0;
    this.defaultMaxCompare = 0;
    this.hasSaleVariants = false;
    this.originalPriceHTML = '';
  }

  connectedCallback() {
    this.defaultMinPrice = parseInt(this.dataset.defaultMinPrice) || 0;
    this.defaultMaxPrice = parseInt(this.dataset.defaultMaxPrice) || 0;
    this.defaultMinCompare = parseInt(this.dataset.defaultMinCompare) || 0;
    this.defaultMaxCompare = parseInt(this.dataset.defaultMaxCompare) || 0;
    this.hasSaleVariants = this.dataset.hasSaleVariants === 'true';
    
    this.originalPriceHTML = this.innerHTML;
    
    const cardWrapper = this.closest('.card-wrapper');
    if (cardWrapper) {
      this.swatches = cardWrapper.querySelectorAll('.card-color-swatch[data-swatch-price-min]');
    }
    
    // Set default price range if prices vary (regardless of sale status)
    if (this.defaultMinPrice !== this.defaultMaxPrice) {
      this.showPriceRange(this.defaultMinPrice, this.defaultMaxPrice);
    }
    
    this.swatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', () => this.handleSwatchHover(swatch));
    });
  }

  disconnectedCallback() {
    this.swatches.forEach(swatch => {
      swatch.removeEventListener('mouseenter', () => this.handleSwatchHover(swatch));
    });
  }

  formatPrice(price) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(price, window.money_format || '{{amount}}');
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price / 100);
  }

  showPriceRange(minPrice, maxPrice) {
    const priceHTML = `
      <div class="price price--range">
        <div>
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Price range</span>
            <span class="price-item price-item--regular">
              ${this.formatPrice(minPrice)} - ${this.formatPrice(maxPrice)}
            </span>
          </div>
        </div>
      </div>
    `;
    this.innerHTML = priceHTML;
  }

  showSalePrice(comparePrice, salePrice) {
    const priceHTML = `
      <div class="price price--on-sale">
        <div>
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price__sale--regular-price">
              <s class="price-item price-item--regular">
                ${this.formatPrice(comparePrice)}
              </s>
            </span>
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last price-item--sale-ember">
              ${this.formatPrice(salePrice)}
            </span>
          </div>
        </div>
      </div>
    `;
    this.innerHTML = priceHTML;
  }

  showRegularPrice(price) {
    const priceHTML = `
      <div class="price">
        <div>
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price-item price-item--regular">
              ${this.formatPrice(price)}
            </span>
          </div>
        </div>
      </div>
    `;
    this.innerHTML = priceHTML;
  }

  handleSwatchHover(swatch) {
    const minPrice = parseInt(swatch.dataset.swatchPriceMin) || 0;
    const minCompare = parseInt(swatch.dataset.swatchCompareMin) || 0;
    const isOnSale = swatch.dataset.swatchOnSale === 'true';

    if (isOnSale && minCompare > 0) {
      this.showSalePrice(minCompare, minPrice);
    } else {
      this.showRegularPrice(minPrice);
    }
  }
}

customElements.define('swatch-price-handler', SwatchPriceHandler);
